import { useEffect, useMemo, useState } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Button } from "reactstrap";
import pluralize from "pluralize";

import { Field, Module } from "../../../types/TenantInterfaces";
import IndeterminateCheckbox from "../../AppTable/IndeterminateCheckbox";
import FieldFormatter from "../Formatters/FieldFormatter";
import AppTableBody from "../../AppTable/AppTableBody";
import AppTableFooters from "../../AppTable/AppTableFooters";
import AppTableHeaders from "../../AppTable/AppTableHeaders";
import StrUtils from "../../../utils/StrUtils";
import { RecordSummaryWidget } from "../../Widgets/RecordSummaryWidget";
import FieldUtils from "../../../utils/FieldUtils";
import IconButton from "../../../elements/IconButton";
import useUpdateRecord from "../../../api/api-hooks/records/useUpdateRecord";
import useDeleteSubitems from "../../../api/api-hooks/records/useDeleteSubitems";
import InstanceUtils from "../../../utils/InstanceUtils";
import { ReusableViewReferenceFields } from "../../../types/PageInterfaces";

interface Props {
  currentValues?: Array<any>;
  fieldSchema: Field;
  parentRecord: any;
  module: Module;
  createMode?: boolean;
  prefill?: any;
  isDuplicating?: boolean;
  onSaveCreate?: (data: any) => void;
  onDeleteCreate?: (itemID: string) => void;
  toggleDuplicateSubitem: () => void;
  setPrefill: (data: any) => void;
  setSubitemAdding: (val: boolean) => void;
  showCloseButton?: boolean;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}

/**
 * Editor for MultiSubitem. Allows you to add/edit multiple Subitems at a time.
 */
const MultiSubitemEditor = ({
  currentValues = [],
  fieldSchema,
  parentRecord,
  module,
  prefill = null,
  createMode = false,
  isDuplicating = false,
  onSaveCreate,
  onDeleteCreate,
  toggleDuplicateSubitem,
  setPrefill,
  setSubitemAdding,
  showCloseButton,
  reusableViewReferenceFields,
}: Props) => {
  const [values, setValues] = useState<Array<any>>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  //@ts-ignore
  const { updateRecord } = useUpdateRecord();
  const { deleteSubitems } = useDeleteSubitems();
  const singularSubitem = fieldSchema.Name
    ? pluralize.singular(fieldSchema.Name)
    : "Subitem";

  const fields = useMemo(
    () =>
      fieldSchema?.SubitemFields?.filter((f) => {
        if (f?.APIName === "Tags") return true;
        if (f?.SystemField) return false;
        return true;
      }) || [],
    [fieldSchema?.SubitemFields]
  );

  const prefilledSubitem = useMemo(() => {
    const payload: any = fieldSchema.SubitemFields?.reduce(
      (accumulator, value: any) => {
        return { ...accumulator, [value.APIName]: "" };
      },
      {}
    );

    payload._ModuleAPIName =
      parentRecord?.APIName || parentRecord?._ModuleAPIName;
    if (prefill?.length) {
      for (const pre of prefill) {
        const key = pre.Field?.includes(".")
          ? pre.Field.split(".", 2)[1]
          : pre.Field;

        payload[key] = pre.Value;
      }
    }

    fieldSchema?.SubitemFields?.forEach((field: any) => {
      if (![null, "", undefined].includes(field?.DefaultValue))
        payload[field.APIName] = field?.DefaultValue;
    });
    return payload;
  }, [prefill]);

  const section = useMemo(() => {
    return {
      Title: null,
      Fields: fields?.map((f) => ({ Field: f, ColSpan: 1 })),
      Type: fieldSchema.FieldType,
      DefaultSubitem: null,
    };
  }, [fields, fieldSchema.FieldType]);

  const columns: any = useMemo(() => {
    if (!section.Fields) return null;

    const columns = [];

    columns.push({
      id: "selectable-rows-radio",
      //@ts-ignore
      cell: ({ row }) => (
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      ),
    });

    for (const f of section.Fields) {
      const field = f.Field;

      columns.push({
        accesorKey: field?.APIName,
        id: field?.APIName,
        //@ts-ignore
        cell: (item) => {
          const row = item.row.original;

          return (
            //@ts-ignore
            <FieldFormatter
              field={field}
              value={row?.[field?.APIName]}
              moduleAPIName={module?.APIName}
              table={true}
              forceReadOnly={true}
              reusableViewReferenceFields={reusableViewReferenceFields}
            />
          );
        },
        header: () => field?.Name,
      });
    }

    return columns;
  }, [section?.Fields, module?.APIName]);

  const saveDisabled = useMemo(() => {
    const requiredFields =
      fieldSchema.SubitemFields?.flatMap((f) =>
        f?.Required ? [f.APIName] : []
      ) || [];

    if (values?.length === 0) return false;

    const compareObj = structuredClone(values[activeIndex]);
    delete compareObj?.id;
    delete compareObj?._ModuleAPIName;

    const isEmpty = Object.values(compareObj).every(
      (x) => x === null || x === "" || x === undefined
    );
    if (isEmpty) return true;

    let requiredFlag = requiredFields.length > 0 ? true : false;
    for (let i = 0; i < requiredFields.length; i++) {
      if (compareObj[requiredFields[i]] !== "") {
        requiredFlag = false;
        i = requiredFields.length;
      }
    }
    return requiredFlag;
  }, [values, activeIndex]);

  useEffect(() => {
    if (!fields) return;
    handleAddSubitem();
  }, [fields]);

  const table = useReactTable({
    data: values,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection: values?.length ? { [activeIndex?.toString()]: true } : {},
    },
    onRowSelectionChange: handleSelectionChange,
  });

  return (
    <>
      <div className="table-responsive mb-2">
        <table style={{ minWidth: "100%" }}>
          {/* @ts-ignore */}
          <AppTableHeaders table={table} />
          <AppTableBody table={table} selectable loading={false} error={null} />
          {/* @ts-ignore */}
          <AppTableFooters table={table} />
        </table>
      </div>
      {values?.length > 0 && (
        <div className="bg-white mt-2 p-3 rounded">
          <div className="d-flex mx-2 justify-content-between">
            <h4 className="mb-0">
              Editing {singularSubitem} {activeIndex + 1}
            </h4>
            <div>
              <IconButton
                onClick={() => setActiveIndex((prev) => prev - 1)}
                icon="chevron-left"
                tooltipText="Last Subitem"
                disabled={activeIndex < 1}
              />
              <IconButton
                onClick={() => setActiveIndex((prev) => prev + 1)}
                icon="chevron-right"
                tooltipText="Next Subitem"
                disabled={activeIndex === values.length - 1}
              />
              <IconButton
                disabled={activeIndex === values.length - 1}
                onClick={handleRemoveSubitem}
                icon="delete-outline"
                tooltipText="Remove Subitem"
                color="danger"
                className=""
              />
            </div>
          </div>
          <div className="mx-2">
            {/*@ts-ignore */}
            <RecordSummaryWidget
              key={values[activeIndex].id}
              module={module}
              section={section}
              convertFields={convertFields}
              values={values[activeIndex]}
              handleChange={(e: any, au: any) => {
                setValues((prev) => {
                  const newInput = prev[activeIndex];
                  newInput[e.APIName] = au;
                  prev[activeIndex] = newInput;
                  return [...prev];
                });
              }}
              createMode={true}
              reusableViewReferenceFields={reusableViewReferenceFields}
            />
          </div>
          <div className="d-flex mx-2 justify-content-end">
            {showCloseButton && (
              <Button onClick={() => setSubitemAdding(false)} size="sm">
                Close
              </Button>
            )}
            {/* @ts-ignore */}
            <Button
              disabled={saveDisabled}
              onClick={asyncSaveCreate}
              color="primary"
              size="sm"
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </>
  );

  async function asyncSaveCreate() {
    if (createMode) {
      if (typeof onSaveCreate === "function") onSaveCreate([...values]);
      handleAddSubitem();
      toggleDuplicateSubitem();
      return setActiveIndex((prev) => prev + 1);
    }
    handleSave(values[activeIndex]);
    if (isDuplicating) {
      setPrefill(null);
      return toggleDuplicateSubitem();
    }
    if (activeIndex === values.length - 1) {
      handleAddSubitem();
      setActiveIndex((prev) => prev + 1);
    }
  }

  function handleAddSubitem() {
    if (!fields) return;
    const newValue: any = structuredClone(prefilledSubitem);

    newValue.id = StrUtils.uuidv4();

    setValues((prev) => {
      return [...prev, newValue];
    });
  }

  async function handleSave(newValue: any) {
    let payload: any =
      Array.isArray(currentValues) && currentValues?.length
        ? [...currentValues]
        : [];

    if (fieldSchema.APIName !== "_RemoteSystems") {
      const parsedValue = await InstanceUtils.formatRecordCreationPayload(
        fieldSchema.SubitemFields,
        newValue
      );
      payload.push(parsedValue);
    } else payload.push(newValue);

    updateRecord({
      field: fieldSchema,
      payload: { [fieldSchema.APIName]: payload },
      moduleAPIName: createMode
        ? fieldSchema.APIName
        : parentRecord._ModuleAPIName,
      recordID: parentRecord.id,
      subitemID: undefined,
      _UserActivity: undefined,
    });
  }

  function handleRemoveSubitem() {
    if (createMode) {
      const newValues = structuredClone(values);
      newValues.splice(activeIndex, 1);
      const filtered = newValues.filter(
        (vals: any) => vals.id !== values[activeIndex].id
      );
      setValues(() => {
        if (typeof onDeleteCreate === "function")
          onDeleteCreate(values[activeIndex].id);
        return filtered;
      });
      return;
    }

    deleteSubitems({
      selectedSubitems: [values[activeIndex].id],
      fieldSchema,
      parentRecord,
      onSuccess: () => {
        setValues((prev) => {
          const newValues = [...prev];
          newValues.splice(activeIndex, 1);
          setActiveIndex((prev2) => {
            if (prev2 === 0) return 0;
            return prev2 - 1;
          });
          return newValues;
        });
      },
    });
  }

  function convertFields(field: any) {
    if (typeof field.Field !== "string") return field;
    const newField = FieldUtils.getFieldFromModule(module, field.Field);
    return { ...field, Field: newField };
  }

  function handleSelectionChange(selection: any) {
    if (!selection || !selection()) return;

    const newSelection = selection();
    const indexes = Object.keys(newSelection);

    if (indexes.length === 0) return;

    setActiveIndex(parseInt(indexes[0]));
  }
};

export default MultiSubitemEditor;
